import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { queryClient } from '@/lib/queryClient';

if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,
    tracesSampleRate: 0.1,
  });
}

// Eager-loaded: critical first-load paths
import LoginPage from '@/pages/LoginPage';
import RegisterPage from '@/pages/RegisterPage';
import ApplyPage from '@/pages/ApplyPage';

// Lazy-loaded: authenticated pages
const DashboardPage = React.lazy(() => import('@/pages/DashboardPage'));
const ReferralsPage = React.lazy(() => import('@/pages/ReferralsPage'));
const EarningsPage = React.lazy(() => import('@/pages/EarningsPage'));
const PayoutsPage = React.lazy(() => import('@/pages/PayoutsPage'));
const MarketingPage = React.lazy(() => import('@/pages/MarketingPage'));
const SettingsPage = React.lazy(() => import('@/pages/SettingsPage'));
const ProductPerformancePage = React.lazy(() => import('@/pages/ProductPerformancePage'));
const PendingApprovalPage = React.lazy(() => import('@/pages/PendingApprovalPage'));

// Admin Pages (lazy)
const AdminPartnersPage = React.lazy(() => import('@/pages/admin/PartnersPage'));
const AdminPayoutsPage = React.lazy(() => import('@/pages/admin/PayoutsPage'));
const AdminOffersPage = React.lazy(() => import('@/pages/admin/OffersPage'));

// Layout
import DashboardLayout from '@/components/layout/DashboardLayout';

// Protected Route Component
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading, isPartner, isSuperAdmin, partner } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Super admins always have access
  if (isSuperAdmin) {
    return <>{children}</>;
  }

  // If not a partner, redirect to apply
  if (!isPartner) {
    return <Navigate to="/apply" replace />;
  }

  // If partner but pending approval
  if (partner?.status === 'pending') {
    return <Navigate to="/pending-approval" replace />;
  }

  // If partner is suspended
  if (partner?.status === 'suspended') {
    return <Navigate to="/suspended" replace />;
  }

  return <>{children}</>;
}

// Admin Route Component
function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, loading, isSuperAdmin } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user || !isSuperAdmin) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function AppRoutes() {
  return (
    <Suspense fallback={
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    }>
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/apply" element={<ApplyPage />} />
      <Route path="/pending-approval" element={<PendingApprovalPage />} />

      {/* Protected partner routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="referrals" element={<ReferralsPage />} />
        <Route path="earnings" element={<EarningsPage />} />
        <Route path="payouts" element={<PayoutsPage />} />
        <Route path="marketing" element={<MarketingPage />} />
        <Route path="products" element={<ProductPerformancePage />} />
        <Route path="settings" element={<SettingsPage />} />

        {/* Admin routes */}
        <Route
          path="admin/partners"
          element={
            <AdminRoute>
              <AdminPartnersPage />
            </AdminRoute>
          }
        />
        <Route
          path="admin/payouts"
          element={
            <AdminRoute>
              <AdminPayoutsPage />
            </AdminRoute>
          }
        />
        <Route
          path="admin/offers"
          element={
            <AdminRoute>
              <AdminOffersPage />
            </AdminRoute>
          }
        />
      </Route>

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <Router>
          <AuthProvider>
            <AppRoutes />
          </AuthProvider>
        </Router>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;

